'use client';

import type * as React from 'react';

/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-no-useless-fragment */
import { Loader } from '@coop/cds-components';
import { useDelayedIsLoading } from '@coop/hooks';
import classnames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { forwardRef, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { VisuallyHidden } from 'react-aria';
import { CSSTransition } from 'react-transition-group';

import { PacmanLoader } from '../PacmanLoader';
import styles from './OverlayLoader.module.scss';

export type OverlayLoaderProps = {
    isLoading: boolean;
    additionalClasses?: string;
    type?: 'balls' | 'pacman';
    delay?: number;
    size?: Parameters<typeof Loader>[0]['size'];
    loadingText?: string;
    /** Use when you want to block user from tabbing under loader. This will also trap focus inside the loader until its done */
    blocking?: boolean;
    timeout?: React.ComponentProps<typeof CSSTransition>['timeout'];
};

export const OverlayLoader = (props: React.PropsWithChildren<OverlayLoaderProps>) => {
    const isLoading = useDelayedIsLoading(props.isLoading, props.delay || 0);
    const loaderId = useId();

    const shouldTrapFocus = isLoading && props.blocking;

    const loadingText = props.loadingText || 'Hämtar data';

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (shouldTrapFocus) {
            ref.current?.focus();
        }
    }, [shouldTrapFocus]);

    return (
        <>
            <CSSTransition
                nodeRef={ref}
                in={isLoading}
                timeout={props.timeout ?? 200}
                classNames={{
                    enter: styles.IsVisible,
                    enterDone: styles.IsVisible,
                }}
                unmountOnExit
                appear
            >
                {props.blocking ? (
                    <FocusTrap
                        active={shouldTrapFocus}
                        focusTrapOptions={{
                            fallbackFocus: `[id="${loaderId}"]`,
                            allowOutsideClick: true,
                        }}
                    >
                        <LoaderContent
                            ref={ref}
                            loaderId={loaderId}
                            isLoading={isLoading}
                            className={props.additionalClasses}
                            loadingText={loadingText}
                            type={props.type}
                            size={props.size}
                        />
                    </FocusTrap>
                ) : (
                    <LoaderContent
                        ref={ref}
                        loaderId={loaderId}
                        isLoading={isLoading}
                        className={props.additionalClasses}
                        loadingText={loadingText}
                        type={props.type}
                        size={props.size}
                    />
                )}
            </CSSTransition>
            <VisuallyHidden
                id={`${loaderId}_announcer`}
                role="status"
                aria-live="polite"
                aria-atomic="true"
            >
                {isLoading ? loadingText : undefined}
            </VisuallyHidden>
        </>
    );
};

const LoaderContent = forwardRef<
    HTMLDivElement,
    {
        loaderId: string;
        ref: React.RefObject<HTMLDivElement | null>;
        isLoading: boolean;
        loadingText: string;
        type?: 'balls' | 'pacman';
        size?: Parameters<typeof Loader>[0]['size'];
        className?: string;
    }
>((props, ref) => {
    return (
        <div
            id={props.loaderId}
            ref={ref}
            tabIndex={props.isLoading ? -1 : undefined}
            className={classnames(styles.LoaderOverlay, props.className)}
            data-testid="loader"
        >
            {props.type === 'pacman' ? (
                <PacmanLoader isLoading={props.isLoading} />
            ) : (
                <Loader isLoading={props.isLoading} size={props.size} ariaHidden />
            )}
        </div>
    );
});

// Native omit does not take discriminated unions into account. This one does. Taken from https://github.com/microsoft/TypeScript/issues/54451
type MappedOmit<T, K extends keyof T> = { [P in keyof T as P extends K ? never : P]: T[P] };

export const OverlayPacmanLoader = ({ ...props }: MappedOmit<OverlayLoaderProps, 'type'>) => {
    return <OverlayLoader {...props} type="pacman" />;
};

export const A11yOverlayLoader = (props: React.PropsWithChildren<OverlayLoaderProps>) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        ref.current?.toggleAttribute('inert', props.isLoading);
    }, [props.isLoading]);

    return (
        <div className="u-posRelative">
            <OverlayLoader {...props} type={props.type ?? 'pacman'} />
            <div ref={ref} className={props.additionalClasses}>
                {props.children}
            </div>
        </div>
    );
};

export const FullOverlayLoaderWrapper = ({
    isLoading,
    children,
}: {
    isLoading: boolean;
    children: React.ReactNode;
}) => {
    return isLoading ? <OverlayLoader isLoading={isLoading} /> : <>{children}</>;
};
